import type { SecretInputValue } from '@/components/atoms/SecretInput';
import type {
    PenRef,
    ProviderFieldSchema,
    ProviderGroup,
    ProviderSchema,
    SecretName,
} from '@/domain/providerConfig';
import { penKey, toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import {
    emptyFormValue,
    type FormFieldValue,
    keepAllSecrets,
    toFormFieldValue,
} from '@/routes/shared/ProviderConfig/form/formValues';

/**
 * One pen of the site. `connected` says the pen has a stored row; a pen without one that
 * the user fills in is created on save. Typed secrets live here, in memory only.
 */
export interface PenFormValues {
    connected: boolean;
    isActive: boolean;
    remove: boolean;
    /** Pen-scope values, plus the pen's own value for a site field where pens differ. */
    fields: Record<string, FormFieldValue>;
    secrets: Record<SecretName, SecretInputValue>;
}

export interface ProviderFormValues {
    connection: Record<string, FormFieldValue>;
    secrets: Record<SecretName, SecretInputValue>;
    pens: Record<string, PenFormValues>;
}

/** The value fields the form edits: no secrets, and advanced ones only when allowed. */
export function formFields(
    schema: ProviderSchema,
    options: { includeAdvanced: boolean }
): ProviderFieldSchema[] {
    return schema.fields.filter(
        (field) => field.kind !== 'secret' && (options.includeAdvanced || !field.advanced)
    );
}

/**
 * The schema a viewer's edits are diffed and judged against. A client admin reads advanced
 * settings but never changes one, so no advanced key reaches their diff, rules or save,
 * after a merge or a reload included; the form still holds the values, for display.
 */
export function editableSchema(schema: ProviderSchema, isOptoScaleAdmin: boolean): ProviderSchema {
    if (isOptoScaleAdmin) return schema;
    return { ...schema, fields: schema.fields.filter((field) => !field.advanced) };
}

function penFields(
    fields: readonly ProviderFieldSchema[],
    group: ProviderGroup | null,
    cageId: number
): Record<string, FormFieldValue> {
    const row = group?.rows.find((candidate) => candidate.cageId === cageId);
    const values: Record<string, FormFieldValue> = {};
    for (const field of fields) {
        if (field.scope === 'pen') {
            values[toFormKey(field.key)] = toFormFieldValue(field, row?.fields[field.key] ?? null);
            continue;
        }
        const connection = group?.connection[field.key];
        if (connection?.varies)
            values[toFormKey(field.key)] = toFormFieldValue(
                field,
                connection.perPen[cageId] ?? null
            );
    }
    return values;
}

/**
 * The form's starting values. A site field the pens disagree on is blank at the site and
 * held under each pen instead; every pen of the site gets an entry, and every secret
 * starts as `keep`.
 */
export function toFormValues(
    schema: ProviderSchema,
    group: ProviderGroup | null,
    pens: PenRef[],
    // Every viewer's form holds every setting; editableSchema decides what it may change.
    options: { includeAdvanced: boolean } = { includeAdvanced: true }
): ProviderFormValues {
    const fields = formFields(schema, options);
    const connection: Record<string, FormFieldValue> = {};
    for (const field of fields) {
        if (field.scope !== 'site') continue;
        const value = group?.connection[field.key];
        connection[toFormKey(field.key)] =
            value === undefined || value.varies
                ? emptyFormValue(field)
                : toFormFieldValue(field, value.value);
    }
    const penValues: Record<string, PenFormValues> = {};
    for (const pen of pens) {
        const row = group?.rows.find((candidate) => candidate.cageId === pen.cageId);
        penValues[penKey(pen.cageId)] = {
            connected: row !== undefined,
            isActive: row?.isActive ?? true,
            remove: false,
            fields: penFields(fields, group, pen.cageId),
            secrets: keepAllSecrets(),
        };
    }
    return { connection, secrets: keepAllSecrets(), pens: penValues };
}
